import { z } from 'zod';
// Alle Balancing-Zahlen. Die Defaults hier sind die Seed-Werte; pro Welt liegen sie flach
// (z. B. "buildings.BROOD.baseCost.nectar") in balance_config und ueberschreiben die Defaults.
export const resourceAmountSchema = z.object({
    nectar: z.number().nonnegative(),
    pollen: z.number().nonnegative(),
    wax: z.number().nonnegative(),
});
export const RESOURCES = ['nectar', 'pollen', 'wax'];
export const buildingBalanceSchema = z.object({
    baseCost: resourceAmountSchema,
    baseTimeSeconds: z.number().positive(),
    maxLevel: z.number().int().positive(),
});
export const unitBalanceSchema = z.object({
    /** Kosten pro Biene */
    cost: resourceAmountSchema,
    /** Ausbildungszeit pro Biene in Sekunden (Brutwabe 0, Welt-Speed 1) */
    trainSeconds: z.number().positive(),
    attack: z.number().nonnegative(),
    defense: z.number().nonnegative(),
    gatherPower: z.number().nonnegative(),
    carry: z.number().nonnegative(),
    minPerHex: z.number().positive(),
    /** Wabenstufen, die vorhanden sein muessen: { WAR_COMB: 1 } */
    requires: z.record(z.string(), z.number().int().nonnegative()),
});
export const fieldBalanceSchema = z.object({
    resource: z.enum(['nectar', 'pollen']),
    /** Ertrag pro Minute bei Alleinbesetzung und prod_mult 1 */
    baseRate: z.number().positive(),
    /** relative Haeufigkeit bei der Weltgenerierung */
    weight: z.number().positive(),
});
export const balanceSchema = z.object({
    production: z.object({
        /** Grundertrag des Stocks ("Hausgarten") pro Minute bei prod_mult 1 */
        nectarPerMinute: z.number().nonnegative(),
        pollenPerMinute: z.number().nonnegative(),
        waxPerMinute: z.number().nonnegative(),
        /** zusaetzlich pro Stufe Wachsdruese */
        waxGlandPerLevelPerMinute: z.number().nonnegative(),
    }),
    storage: z.object({
        /** Lagerkapazitaet je Ressource bei Stufe 0 */
        base: z.number().positive(),
        /** zusaetzlich pro Stufe des zustaendigen Lagers (Honigraum: Nektar+Wachs, Pollenlager: Pollen) */
        perLevel: z.number().nonnegative(),
    }),
    start: z.object({
        resources: resourceAmountSchema,
        buildings: z.record(z.string(), z.number().int().nonnegative()),
        units: z.record(z.string(), z.number().int().nonnegative()),
    }),
    population: z.object({
        /** Bevoelkerungslimit bei Brutwabe 0 */
        base: z.number().int().nonnegative(),
        perBroodLevel: z.number().int().nonnegative(),
    }),
    train: z.object({
        queueSlots: z.number().int().positive(),
        broodSpeedPerLevel: z.number().nonnegative(),
        /** Ammen beschleunigen die Ausbildung: Bonus pro Amme, gedeckelt */
        nurseSpeedPerUnit: z.number().nonnegative(),
        nurseSpeedMax: z.number().nonnegative(),
    }),
    fields: z.object({
        RAPESEED: fieldBalanceSchema,
        ORCHARD: fieldBalanceSchema,
        MEADOW: fieldBalanceSchema,
        LINDEN: fieldBalanceSchema,
        HEATHER: fieldBalanceSchema,
    }),
    map: z.object({
        /** Radius (Hex) der generierten Welt */
        radius: z.number().int().positive(),
        /** Anteil der Hexfelder, die ein Bluetenfeld tragen */
        fieldDensity: z.number().min(0).max(1),
        /** Kein Feld naeher als so viele Hex an einem anderen Feld */
        fieldMinSpacing: z.number().int().nonnegative(),
        /** Anteil der Hexfelder (ohne Felder), auf denen ein unbewohnter Stockplatz liegt */
        hiveDensity: z.number().min(0).max(1),
        /** Mindestabstand zwischen Stockplaetzen (Hex) */
        hiveMinSpacing: z.number().int().nonnegative(),
    }),
    diplomacy: z.object({
        /** Verschlechterung einer Beziehung (Verbuendet -> Neutral/Feindlich, Neutral -> Feindlich) greift erst nach dieser Frist */
        downgradeCooldownHours: z.number().nonnegative(),
        maxMembers: z.number().int().positive(),
        /** Nachrichten pro Minute und Spieler */
        chatRatePerMinute: z.number().int().positive(),
    }),
    combat: z.object({
        /** Verteidigungsbonus der Waechterwabe pro Stufe (nur im Stock) */
        guardCombBonusPerLevel: z.number().nonnegative(),
        /** Exponent der Verlustkurve: Verlierer 100 %, Gewinner (1/r)^e */
        lossExponent: z.number().positive(),
        /** Heizerinnen-Verteidigung zaehlt gegen diese Angreifertypen mit Faktor (fehlend = 1) */
        heaterVs: z.record(z.string(), z.number().nonnegative()),
        /** Verliert der Verteidiger mindestens diesen Anteil seiner Bienen, bekommt der Stock ein Schild */
        shieldLossThreshold: z.number().min(0).max(1),
        shieldHours: z.number().nonnegative(),
        /** Wer mehr als das X-fache der Punkte des Ziels hat, darf nicht angreifen, solange das Ziel unter minPoints liegt */
        pointRatioLimit: z.number().positive(),
        pointRatioMinPoints: z.number().nonnegative(),
        /** Anteil des Lagers, der maximal erbeutet werden kann */
        maxLootFraction: z.number().min(0).max(1),
    }),
    queen: z.object({
        /** Kosten einer Weisellarve */
        cost: resourceAmountSchema,
        /** Reifezeit der Larve bei Weiselzelle 1 (Sekunden, vor Weltgeschwindigkeit) */
        breedSeconds: z.number().positive(),
        /** Jede weitere Weiselzellen-Stufe verkuerzt die Reifezeit um diesen Anteil (multiplikativ) */
        cellSpeedPerLevel: z.number().min(0).max(1),
        /** Reservekoeniginnen je Weiselzellen-Stufe */
        reservePerCellLevel: z.number().int().positive(),
        /** Frist der Weisellosigkeit, danach schluepft eine Notkoenigin */
        queenlessHours: z.number().positive(),
        /** Produktion ohne Koenigin */
        queenlessProdMult: z.number().min(0).max(1),
        /** Anteil der Bienen, der beim Notschlupf verloren geht */
        emergencyLossFraction: z.number().min(0).max(1),
        /** p(Koenigin faellt) = clamp(killBase + killScale * (1 - 1/r), 0, killMax), nur bei gewonnenem Raubzug */
        killBase: z.number().min(0).max(1),
        killScale: z.number().min(0).max(1),
        killMax: z.number().min(0).max(1),
    }),
    quests: z.object({
        /** Belohnung je Startquest (wird dem aktiven Stock gutgeschrieben, am Lager gekappt) */
        rewards: z.record(z.string(), resourceAmountSchema),
    }),
    ticker: z.object({
        /** Ab so vielen gefallenen Bienen (beide Seiten) landet ein Raubzug im Welt-Ticker */
        bigFightLosses: z.number().int().nonnegative(),
        /** Anzahl Eintraege, die world.ticker hoechstens liefert */
        maxEntries: z.number().int().positive(),
    }),
    winter: z.object({
        /** verbrauch_pro_stunde = population * consumptionPerBeePerHour * welt_speed (aus dem Vorrat, Reihenfolge consumeOrder) */
        consumptionPerBeePerHour: z.number().nonnegative(),
        consumeOrder: z.array(z.enum(['nectar', 'pollen', 'wax'])).min(1),
        /** Abstand der Winter-Ticks in Stunden (Weltzeit) */
        tickHours: z.number().positive(),
        /** Nach so vielen Stunden Hunger am Stueck verfaellt die hoechste Wabe um eine Stufe (dann von vorn) */
        decayBuildingAfterHours: z.number().positive(),
        /** Fruehjahrs-Erholung: Anteil der im Winter verhungerten Bienen, der beim Winterende als Sammlerinnen zurueckkehrt */
        recoveryFraction: z.number().min(0).max(1),
    }),
    colonize: z.object({
        /** mindest_hofstaat = minCourtBase + zielpunkte * minCourtPerTargetPoint */
        minCourtBase: z.number().nonnegative(),
        minCourtPerTargetPoint: z.number().nonnegative(),
        /** verlust = hofstaat * baseLossRate * saison * (1 + restbevoelkerung * lossPerRemainingPop + guardComb * lossPerGuardCombLevel) */
        baseLossRate: z.number().min(0).max(1),
        lossPerRemainingPop: z.number().nonnegative(),
        lossPerGuardCombLevel: z.number().nonnegative(),
        /** Schutzschild des uebernommenen Stocks gegen Rueckeroberung (Stunden) */
        takeoverShieldHours: z.number().nonnegative(),
        /** Startwaben einer neuen Kolonie auf freiem Hex */
        newHiveBuildings: z.record(z.string(), z.number().int().nonnegative()),
        /** Mindestabstand einer neuen Kolonie zu anderen Stoecken (Hex) */
        minDistanceToHives: z.number().int().nonnegative(),
    }),
    units: z.object({
        FORAGER: unitBalanceSchema,
        SCOUT: unitBalanceSchema,
        GUARD: unitBalanceSchema,
        WARRIOR: unitBalanceSchema,
        HEATER: unitBalanceSchema,
        NURSE: unitBalanceSchema,
    }),
    build: z.object({
        queueSlots: z.number().int().positive(),
        costGrowth: z.number().positive(),
        timeGrowth: z.number().positive(),
        broodSpeedPerLevel: z.number().nonnegative(),
    }),
    buildings: z.object({
        BROOD: buildingBalanceSchema,
        HONEY_STORE: buildingBalanceSchema,
        POLLEN_STORE: buildingBalanceSchema,
        WAX_GLAND: buildingBalanceSchema,
        GUARD_COMB: buildingBalanceSchema,
        WAR_COMB: buildingBalanceSchema,
        QUEEN_CELL: buildingBalanceSchema,
        DANCE_FLOOR: buildingBalanceSchema,
    }),
});
export const DEFAULT_BALANCE = {
    production: {
        nectarPerMinute: 4,
        pollenPerMinute: 3,
        waxPerMinute: 1,
        waxGlandPerLevelPerMinute: 0.5,
    },
    storage: { base: 1500, perLevel: 1000 },
    start: {
        resources: { nectar: 600, pollen: 500, wax: 200 },
        buildings: { BROOD: 1, HONEY_STORE: 1, POLLEN_STORE: 1 },
        units: { FORAGER: 200, GUARD: 50, NURSE: 50 },
    },
    population: { base: 300, perBroodLevel: 120 },
    diplomacy: { downgradeCooldownHours: 12, maxMembers: 30, chatRatePerMinute: 20 },
    combat: {
        guardCombBonusPerLevel: 0.05,
        lossExponent: 1.5,
        heaterVs: { WARRIOR: 3, FORAGER: 0, SCOUT: 0 },
        shieldLossThreshold: 0.5,
        shieldHours: 6,
        pointRatioLimit: 5,
        pointRatioMinPoints: 500,
        maxLootFraction: 0.75,
    },
    fields: {
        RAPESEED: { resource: 'nectar', baseRate: 40, weight: 4 },
        MEADOW: { resource: 'nectar', baseRate: 25, weight: 6 },
        LINDEN: { resource: 'nectar', baseRate: 60, weight: 1.5 },
        ORCHARD: { resource: 'pollen', baseRate: 35, weight: 4 },
        HEATHER: { resource: 'pollen', baseRate: 50, weight: 1.5 },
    },
    map: { radius: 50, fieldDensity: 0.4, fieldMinSpacing: 0, hiveDensity: 0.06, hiveMinSpacing: 2 },
    quests: {
        rewards: {
            build_brood: { nectar: 200, pollen: 150, wax: 50 },
            train_foragers: { nectar: 150, pollen: 150, wax: 0 },
            first_harvest: { nectar: 300, pollen: 200, wax: 50 },
            train_guards: { nectar: 200, pollen: 200, wax: 100 },
            join_alliance: { nectar: 400, pollen: 300, wax: 150 },
        },
    },
    ticker: { bigFightLosses: 300, maxEntries: 50 },
    winter: {
        consumptionPerBeePerHour: 0.002,
        consumeOrder: ['nectar', 'pollen'],
        tickHours: 1,
        decayBuildingAfterHours: 12,
        recoveryFraction: 0.25,
    },
    colonize: {
        minCourtBase: 800,
        minCourtPerTargetPoint: 0.35,
        baseLossRate: 0.3,
        lossPerRemainingPop: 0.001,
        lossPerGuardCombLevel: 0.1,
        takeoverShieldHours: 6,
        newHiveBuildings: { BROOD: 1 },
        minDistanceToHives: 2,
    },
    queen: {
        cost: { nectar: 800, pollen: 600, wax: 150 },
        breedSeconds: 4 * 3600,
        cellSpeedPerLevel: 0.15,
        reservePerCellLevel: 1,
        queenlessHours: 48,
        queenlessProdMult: 0.5,
        emergencyLossFraction: 0.25,
        killBase: 0.15,
        killScale: 0.45,
        killMax: 0.85,
    },
    train: { queueSlots: 5, broodSpeedPerLevel: 0.06, nurseSpeedPerUnit: 0.002, nurseSpeedMax: 0.3 },
    units: {
        FORAGER: {
            cost: { nectar: 30, pollen: 20, wax: 0 },
            trainSeconds: 20,
            attack: 2,
            defense: 5,
            gatherPower: 1,
            carry: 20,
            minPerHex: 6,
            requires: {},
        },
        SCOUT: {
            cost: { nectar: 40, pollen: 20, wax: 5 },
            trainSeconds: 25,
            attack: 1,
            defense: 3,
            gatherPower: 0,
            carry: 5,
            minPerHex: 3,
            requires: { DANCE_FLOOR: 1 },
        },
        GUARD: {
            cost: { nectar: 50, pollen: 30, wax: 10 },
            trainSeconds: 35,
            attack: 8,
            defense: 25,
            gatherPower: 0,
            carry: 0,
            minPerHex: 8,
            requires: { GUARD_COMB: 1 },
        },
        WARRIOR: {
            cost: { nectar: 70, pollen: 40, wax: 15 },
            trainSeconds: 45,
            attack: 30,
            defense: 12,
            gatherPower: 0,
            carry: 10,
            minPerHex: 7,
            requires: { WAR_COMB: 1 },
        },
        HEATER: {
            cost: { nectar: 60, pollen: 50, wax: 10 },
            trainSeconds: 40,
            attack: 3,
            defense: 15,
            gatherPower: 0,
            carry: 0,
            minPerHex: 9,
            requires: { BROOD: 3 },
        },
        NURSE: {
            cost: { nectar: 35, pollen: 35, wax: 5 },
            trainSeconds: 30,
            attack: 0,
            defense: 2,
            gatherPower: 0,
            carry: 0,
            minPerHex: 8,
            requires: { BROOD: 2 },
        },
    },
    build: { queueSlots: 3, costGrowth: 1.6, timeGrowth: 1.45, broodSpeedPerLevel: 0.06 },
    buildings: {
        BROOD: { baseCost: { nectar: 120, pollen: 100, wax: 30 }, baseTimeSeconds: 60, maxLevel: 30 },
        HONEY_STORE: {
            baseCost: { nectar: 100, pollen: 60, wax: 40 },
            baseTimeSeconds: 75,
            maxLevel: 30,
        },
        POLLEN_STORE: {
            baseCost: { nectar: 90, pollen: 70, wax: 40 },
            baseTimeSeconds: 75,
            maxLevel: 30,
        },
        WAX_GLAND: {
            baseCost: { nectar: 150, pollen: 120, wax: 20 },
            baseTimeSeconds: 120,
            maxLevel: 25,
        },
        GUARD_COMB: {
            baseCost: { nectar: 200, pollen: 150, wax: 80 },
            baseTimeSeconds: 180,
            maxLevel: 25,
        },
        WAR_COMB: {
            baseCost: { nectar: 250, pollen: 200, wax: 100 },
            baseTimeSeconds: 240,
            maxLevel: 25,
        },
        QUEEN_CELL: {
            baseCost: { nectar: 400, pollen: 300, wax: 200 },
            baseTimeSeconds: 600,
            maxLevel: 10,
        },
        DANCE_FLOOR: {
            baseCost: { nectar: 180, pollen: 120, wax: 60 },
            baseTimeSeconds: 150,
            maxLevel: 20,
        },
    },
};
/** Welche Wabe begrenzt welches Lager. */
export const STORAGE_BUILDING = {
    nectar: 'HONEY_STORE',
    wax: 'HONEY_STORE',
    pollen: 'POLLEN_STORE',
};
export const BUILDING_LABELS = {
    BROOD: 'Brutwabe',
    HONEY_STORE: 'Honigraum',
    POLLEN_STORE: 'Pollenlager',
    WAX_GLAND: 'Wachsdrüse',
    GUARD_COMB: 'Wächterwabe',
    WAR_COMB: 'Kriegswabe',
    QUEEN_CELL: 'Weiselzelle',
    DANCE_FLOOR: 'Tanzboden',
};
export const UNIT_LABELS = {
    FORAGER: 'Sammlerin',
    SCOUT: 'Späherin',
    GUARD: 'Wächterin',
    WARRIOR: 'Kriegerin',
    HEATER: 'Heizerin',
    NURSE: 'Amme',
};
export const FIELD_LABELS = {
    RAPESEED: 'Rapsfeld',
    ORCHARD: 'Obstgarten',
    MEADOW: 'Blumenwiese',
    LINDEN: 'Lindenallee',
    HEATHER: 'Heide',
};
export const ROLE_LABELS = {
    LEADER: 'Königin des Stands',
    COUNCIL: 'Rat',
    DIPLOMAT: 'Diplomatin',
    MEMBER: 'Mitglied',
    APPLICANT: 'Bewerbung',
};
export const RELATION_LABELS = {
    ALLIED: 'Verbündet',
    NEUTRAL: 'Neutral',
    HOSTILE: 'Feindlich',
};
export const REPORT_LABELS = {
    RAID_ATTACK: 'Raubzug',
    RAID_DEFENSE: 'Verteidigung',
    SCOUT: 'Spähbericht',
    SCOUT_DEFENSE: 'Späherinnen abgewehrt',
    FIELD_COMBAT: 'Feldkampf',
    QUEEN: 'Königin',
    COLONIZE: 'Kolonisation',
    WINTER: 'Winter',
};
export const REPORT_REASON_LABELS = {
    SPAM: 'Spam',
    HARASSMENT: 'Belästigung',
    CHEATING: 'Betrug / Mehrfachkonto',
    OTHER: 'Sonstiges',
};
export const QUEST_LABELS = {
    build_brood: {
        title: 'Brutwabe auf Stufe 2',
        hint: 'Im Stock unter „Waben“ die Brutwabe ausbauen.',
    },
    train_foragers: {
        title: 'Sammlerinnen ausbilden',
        hint: 'Unter „Bienen“ Sammlerinnen in die Ausbildung geben.',
    },
    first_harvest: {
        title: 'Erstes Feld beernten',
        hint: 'Auf der Karte ein Blütenfeld antippen und Sammlerinnen losschicken – der Ertrag zählt bei der Heimkehr.',
    },
    train_guards: {
        title: 'Wächterinnen ausbilden',
        hint: 'Wächterinnen verteidigen den Stock; ausbilden unter „Bienen“.',
    },
    join_alliance: {
        title: 'Einem Stand beitreten',
        hint: 'Unter „Stand“ bewerben oder einen eigenen Stand gründen.',
    },
};
export const QUEEN_STATE_LABELS = {
    LARVA: 'Weisellarve',
    RULING: 'regiert',
    RESERVE: 'Reserve',
    IN_TRANSIT: 'unterwegs',
    DEAD: 'gestorben',
};
export const RESOURCE_LABELS = {
    nectar: 'Nektar',
    pollen: 'Pollen',
    wax: 'Wachs',
};
/** Verschachteltes Objekt -> flache "a.b.c"-Schluessel (Blaetter = Nicht-Objekte). */
export function flattenBalance(obj, prefix = '') {
    const out = {};
    if (obj !== null && typeof obj === 'object' && !Array.isArray(obj)) {
        for (const [k, v] of Object.entries(obj)) {
            Object.assign(out, flattenBalance(v, prefix ? `${prefix}.${k}` : k));
        }
        return out;
    }
    out[prefix] = obj;
    return out;
}
function setPath(target, path, value) {
    const parts = path.split('.');
    let cur = target;
    for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i];
        const next = cur[p];
        if (next === null || typeof next !== 'object')
            cur[p] = {};
        cur = cur[p];
    }
    cur[parts[parts.length - 1]] = value;
}
/**
 * Defaults + Overrides aus balance_config -> validierte Balance.
 * Ungueltige Overrides werfen, damit ein Tippfehler im Admin-UI nicht still Formeln kippt.
 */
export function mergeBalance(rows, base = DEFAULT_BALANCE) {
    const merged = structuredClone(base);
    for (const row of rows)
        setPath(merged, row.key, row.value);
    return balanceSchema.parse(merged);
}
//# sourceMappingURL=balance.js.map